import * as React from 'react';

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: { readonly transcript: string; readonly confidence: number };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface UseVoiceInputOptions {
  onTranscript: (text: string) => void;
  onSilenceTimeout?: () => void;
  silenceDelayMs?: number;
  lang?: string;
}

interface UseVoiceInputResult {
  isRecording: boolean;
  isSupported: boolean;
  startRecording: () => void;
  stopRecording: () => void;
}

const DEFAULT_SILENCE_DELAY_MS = 2000;

export function useVoiceInput({
  onTranscript,
  onSilenceTimeout,
  silenceDelayMs = DEFAULT_SILENCE_DELAY_MS,
  lang = 'en-US',
}: UseVoiceInputOptions): UseVoiceInputResult {
  const recognitionRef = React.useRef<SpeechRecognitionInstance | null>(null);
  const silenceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hasSpokenRef = React.useRef(false);
  const stoppingRef = React.useRef(false);
  const [isRecording, setIsRecording] = React.useState(false);

  const onSilenceTimeoutRef = React.useRef(onSilenceTimeout);
  onSilenceTimeoutRef.current = onSilenceTimeout;

  const isSupported =
    typeof window !== 'undefined' &&
    (!!window.webkitSpeechRecognition || !!window.SpeechRecognition);

  const clearSilenceTimer = React.useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const startSilenceTimer = React.useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      if (hasSpokenRef.current && onSilenceTimeoutRef.current) {
        stoppingRef.current = true;
        recognitionRef.current?.stop();
        setIsRecording(false);
        onSilenceTimeoutRef.current();
      }
    }, silenceDelayMs);
  }, [clearSilenceTimer, silenceDelayMs]);

  const startRecording = React.useCallback(() => {
    if (!isSupported) return;

    const Ctor = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!Ctor) return;

    hasSpokenRef.current = false;
    stoppingRef.current = false;

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = lang;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        hasSpokenRef.current = true;
        onTranscript(transcript);
        startSilenceTimer();
      }
    };

    recognition.onerror = () => {
      stoppingRef.current = true;
      clearSilenceTimer();
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (!stoppingRef.current && recognitionRef.current === recognition) {
        try {
          recognition.start();
          return;
        } catch {
          // Fall through to stop
        }
      }
      clearSilenceTimer();
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, [isSupported, lang, onTranscript, startSilenceTimer, clearSilenceTimer]);

  const stopRecording = React.useCallback(() => {
    stoppingRef.current = true;
    clearSilenceTimer();
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, [clearSilenceTimer]);

  React.useEffect(
    () => () => {
      stoppingRef.current = true;
      clearSilenceTimer();
      recognitionRef.current?.stop();
    },
    [clearSilenceTimer],
  );

  return { isRecording, isSupported, startRecording, stopRecording };
}
